import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiPost } from './api';
import { STORES_API_BASE, isNotAvailable, useEffectiveStoreNo } from './useStores';

export type Broadcast = {
  id: string | number;
  title: string;
  body: string;
  created_at: string;
};

export type HomeSummary = {
  today: { pending_phases: number };
  questions_open: number;
  unread: { training: number; promotions: number; recognition: number };
  broadcasts: Broadcast[];
};

export const HOME_SUMMARY_KEY = ['stores', 'home-summary'] as const;

export function useHomeSummary(): UseQueryResult<HomeSummary | null, Error> {
  const storeNo = useEffectiveStoreNo();
  return useQuery<HomeSummary | null, Error>({
    queryKey: [...HOME_SUMMARY_KEY, storeNo ?? 'own'],
    queryFn: async () => {
      const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
      try {
        return await apiGet<HomeSummary>(`${STORES_API_BASE}/home-summary${qs}`);
      } catch (err) {
        if (isNotAvailable(err)) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useMarkAreaSeen(area: 'training' | 'promotions' | 'recognition') {
  const qc = useQueryClient();
  const areaRef = useRef(area);
  const storeNo = useEffectiveStoreNo();
  useEffect(() => {
    const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
    void apiPost<void>(`${STORES_API_BASE}/areas/${areaRef.current}/seen${qs}`, {})
      .then(() => qc.invalidateQueries({ queryKey: HOME_SUMMARY_KEY }))
      .catch(() => {});
  }, [qc, storeNo]);
}
